import { isDevelopment, isStaging } from '@/lib/appConstants';
import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * APL-11850 — v4 display-preferences store (theme + accommodations).
 *
 * Drives the v4 token scopes in `src/styles/tokens.css` by swapping the
 * `.dark` class / `data-theme="hc"` / `data-theme="pastel"` /
 * `data-theme="funky"` attribute on `<html>`. CSS variables
 * re-resolve on swap — no re-render storm, tokens only (spec § Data flow).
 *
 * - `mode` is the user's CHOICE (persisted to localStorage); `resolvedTheme`
 *   is what is actually applied ('system' resolves via `prefers-contrast`,
 *   then `prefers-color-scheme`, with change listeners).
 * - DEFAULT is 'light' (fix round G): first-boot users were confused by
 *   'system' auto-dark. 'system' stays available as an EXPLICIT choice in the
 *   theme dropdown (a11y users who set OS-level contrast/scheme prefs).
 * - Corrupt/unknown stored value degrades to the default (spec § Error
 *   handling, revised fix round G — same rationale as the default).
 * - `initTheme()` is called ONCE by the v4 shell (App, Slice 5). The v1
 *   antd shell never calls it, so v3 users never get theme classes — or any
 *   root display styles — on <html>.
 *
 * Display accommodations (run K — spec § Token bridge → Display
 * accommodations): two root settings that scale the whole v4 surface.
 * - `fontScale` (%): applied as an inline `font-size: N%` on <html>; every
 *   rem-based size (all Tailwind sizes) scales proportionally. At the 100%
 *   default the property stays UNSET so v3 renders untouched.
 * - `letterSpacing`: applied as the `--ap-letter-spacing` custom property on
 *   <html>; the preflight `html` rule applies it (src/styles/reset.css) and
 *   `tracking-*` utilities derive from it (src/styles/index.css). The
 *   token's 0em default lives in tokens.css `:root`.
 * - `lineHeight`: applied as `ap-line-relaxed` / `ap-line-loose` class on
 *   <html> ('normal' = no class); tokens.css overrides the Tailwind v4
 *   `--text-*--line-height` theme vars under those classes, so every `text-*`
 *   utility without an explicit `leading-*` re-leads app-wide.
 * Both are v4-surface-only until the take-flow migrates; server-generated
 * PDFs need the values passed explicitly (print/export island integration
 * point — see src/MIGRATION.md).
 *
 * Direction is NOT a preference anymore (accessibility-modal rework): it
 * derives from the active locale only (ar_AR → rtl) via
 * `src/lib/hooks/useEffectiveDirection`. A legacy stored `direction` field is
 * silently dropped on the next prefs heal-write.
 *
 * `fontFamily` (accessibility-modal rework): device-local reading-font
 * override — applied like the theme classes, as `ap-font-pref` class +
 * `--ap-font-pref` var on <html>; tokens.css swaps `--ap-font-sans` under it.
 * @font-face for Lexie Readable / Open-Dyslexic / SF Pro Display lives in
 * public/fonts/accessibility/accessibility-fonts.css (linked in index.html).
 */

export type ThemeMode = 'light' | 'dark' | 'high-contrast' | 'pastel' | 'funky' | 'system';
export type ResolvedTheme = Exclude<ThemeMode, 'system'>;
export type LetterSpacing = 'normal' | 'wide' | 'wider';
export type LineHeight = 'normal' | 'relaxed' | 'loose';
export type FontFamilyPref = 'default' | 'lexie-readable' | 'open-dyslexic' | 'sf-pro-display';

export const THEME_STORAGE_KEY = 'ap-theme-mode';
export const DISPLAY_PREFS_STORAGE_KEY = 'ap-display-prefs';

/** Allowed font-scale stops (%) — mirrors the A−/A+ stepper in the Display settings modal. */
export const FONT_SCALE_STEPS = [85, 92.5, 100, 107.5, 115, 125, 137.5, 150] as const;
export const DEFAULT_FONT_SCALE = 100;

const VALID_MODES: readonly ThemeMode[] = [
  'light',
  'dark',
  'high-contrast',
  'pastel',
  'funky',
  'system',
];

/** First-boot / fallback mode — 'light', never 'system' (fix round G). */
const DEFAULT_MODE: ThemeMode = 'light';

const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && (VALID_MODES as readonly string[]).includes(value);

/** Pastel/Funky are dev/staging-only (accessibility-modal rework) — same
 *  env family the netDebug exclusion uses. */
const EXTENDED_THEME_MODES: readonly ThemeMode[] = ['pastel', 'funky'];
// Dev decision (2026-08): demo counts as PROD for this filter — intentional, do not add isDemo.
const extendedThemesEnabled = (): boolean => isDevelopment || isStaging;

/** The modes the Display modal may offer in this build. */
export const getAvailableThemeModes = (): ThemeMode[] =>
  VALID_MODES.filter((mode) => extendedThemesEnabled() || !EXTENDED_THEME_MODES.includes(mode));

/** Env-gate a mode: an extended mode on a prod build silently falls back to
 *  'system' (NOT the corrupt-value 'light' default — the user made a real
 *  choice on another env; follow the OS until they re-pick). */
const gateThemeMode = (mode: ThemeMode): ThemeMode =>
  EXTENDED_THEME_MODES.includes(mode) && !extendedThemesEnabled() ? 'system' : mode;

const VALID_LETTER_SPACINGS: readonly LetterSpacing[] = ['normal', 'wide', 'wider'];
const DEFAULT_LETTER_SPACING: LetterSpacing = 'normal';

/** Inline values for `--ap-letter-spacing`; 'normal' clears back to the token default (0em). */
const LETTER_SPACING_EM: Record<Exclude<LetterSpacing, 'normal'>, string> = {
  wide: '0.05em',
  wider: '0.1em',
};

const isLetterSpacing = (value: unknown): value is LetterSpacing =>
  typeof value === 'string' && (VALID_LETTER_SPACINGS as readonly string[]).includes(value);

const VALID_LINE_HEIGHTS: readonly LineHeight[] = ['normal', 'relaxed', 'loose'];
const DEFAULT_LINE_HEIGHT: LineHeight = 'normal';

/** <html> classes consumed by tokens.css; 'normal' clears back to the stock leading. */
const LINE_HEIGHT_CLASS: Record<Exclude<LineHeight, 'normal'>, string> = {
  relaxed: 'ap-line-relaxed',
  loose: 'ap-line-loose',
};

const isLineHeight = (value: unknown): value is LineHeight =>
  typeof value === 'string' && (VALID_LINE_HEIGHTS as readonly string[]).includes(value);

const isFontScaleStep = (value: unknown): value is number =>
  typeof value === 'number' && (FONT_SCALE_STEPS as readonly number[]).includes(value);

const VALID_FONT_FAMILIES: readonly FontFamilyPref[] = [
  'default',
  'lexie-readable',
  'open-dyslexic',
  'sf-pro-display',
];
const DEFAULT_FONT_FAMILY: FontFamilyPref = 'default';

export const FONT_FAMILY_CLASS = 'ap-font-pref';
export const FONT_FAMILY_VAR = '--ap-font-pref';

/** Primary stacks only — tokens.css appends --ap-font-sans-default as the
 *  fallback chain. SF Pro Display resolves local()/woff first (see
 *  accessibility-fonts.css) with system-font fallback on non-Apple platforms. */
const FONT_FAMILY_STACKS: Record<Exclude<FontFamilyPref, 'default'>, string> = {
  'lexie-readable': "'Lexie Readable'",
  'open-dyslexic': "'Open-Dyslexic'",
  'sf-pro-display': "'SF Pro Display', -apple-system, system-ui",
};

/** Exported so selectDyslexiaFont's grant-suppression predicate matches exactly. */
export const isFontFamilyPref = (value: unknown): value is FontFamilyPref =>
  typeof value === 'string' && (VALID_FONT_FAMILIES as readonly string[]).includes(value);

/** Snap any runtime value to the nearest allowed step (out-of-range clamps to an end). */
const snapFontScale = (value: number): number => {
  if (!Number.isFinite(value)) return DEFAULT_FONT_SCALE;
  return FONT_SCALE_STEPS.reduce((best, step) =>
    Math.abs(step - value) < Math.abs(best - value) ? step : best
  );
};

/** Remote, schemaless input: an unknown value resolves to `undefined`, so the field inherits the stored Display pref, never a default. */
export const sanitizeDisplayOverride = (override: DisplayOverride | null): DisplayOverride | null =>
  override
    ? {
        // gateThemeMode too: a row authored on staging must not apply an extended theme on a prod build.
        theme: isThemeMode(override.theme) ? gateThemeMode(override.theme) : undefined,
        fontScale:
          typeof override.fontScale === 'number' && Number.isFinite(override.fontScale)
            ? snapFontScale(override.fontScale)
            : undefined,
        letterSpacing: isLetterSpacing(override.letterSpacing) ? override.letterSpacing : undefined,
        lineHeight: isLineHeight(override.lineHeight) ? override.lineHeight : undefined,
        fontFamily: isFontFamilyPref(override.fontFamily) ? override.fontFamily : undefined,
        suppressDeviceFontPref: override.suppressDeviceFontPref === true,
      }
    : null;

const canMatchMedia = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const prefersDark = (): boolean =>
  canMatchMedia() && window.matchMedia('(prefers-color-scheme: dark)').matches;

const prefersMoreContrast = (): boolean =>
  canMatchMedia() && window.matchMedia('(prefers-contrast: more)').matches;

/** 'system' resolves contrast first (accessibility beats aesthetics), then
 *  scheme. 'pastel'/'funky' are explicit-only — no OS preference maps to them. */
const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  if (mode !== 'system') return mode;
  if (prefersMoreContrast()) return 'high-contrast';
  return prefersDark() ? 'dark' : 'light';
};

/** Read + validate the persisted mode; nothing stored → 'light' (the
 *  default); corrupt/unknown values reset to 'light'; a stored extended mode
 *  on a prod build falls back to 'system' silently (storage untouched, so a
 *  dev/staging visit keeps the choice). */
const readStoredMode = (): ThemeMode => {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === null) return DEFAULT_MODE;
    if (isThemeMode(raw)) return gateThemeMode(raw);
    window.localStorage.setItem(THEME_STORAGE_KEY, DEFAULT_MODE);
    return DEFAULT_MODE;
  } catch {
    // Storage unavailable (private mode / disabled) — run on defaults.
    return DEFAULT_MODE;
  }
};

const persistMode = (mode: ThemeMode): void => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Non-fatal: theme still applies for the session.
  }
};

/** Swap the token scope on <html>. */
const applyTheme = (resolved: ResolvedTheme): void => {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.classList.toggle('dark', resolved === 'dark');
  if (resolved === 'high-contrast') {
    el.setAttribute('data-theme', 'hc');
  } else if (resolved === 'pastel' || resolved === 'funky') {
    el.setAttribute('data-theme', resolved);
  } else {
    el.removeAttribute('data-theme');
  }
};

interface DisplayPrefs {
  fontScale: number;
  letterSpacing: LetterSpacing;
  lineHeight: LineHeight;
  fontFamily: FontFamilyPref;
}

const DEFAULT_DISPLAY_PREFS: DisplayPrefs = {
  fontScale: DEFAULT_FONT_SCALE,
  letterSpacing: DEFAULT_LETTER_SPACING,
  lineHeight: DEFAULT_LINE_HEIGHT,
  fontFamily: DEFAULT_FONT_FAMILY,
};

const persistDisplayPrefs = (prefs: DisplayPrefs): void => {
  try {
    window.localStorage.setItem(DISPLAY_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Non-fatal: the accommodation still applies for the session.
  }
};

/** Read + validate persisted prefs; each corrupt/unknown field resets to its
 *  default and the healed shape is written back. A legacy `direction` field
 *  (removed pref — direction is locale-derived now) is dropped by the same
 *  heal-write: silent migration. */
const readStoredDisplayPrefs = (): DisplayPrefs => {
  try {
    const raw = window.localStorage.getItem(DISPLAY_PREFS_STORAGE_KEY);
    if (raw === null) return DEFAULT_DISPLAY_PREFS;
    const parsed: unknown = JSON.parse(raw);
    const record =
      typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
    const prefs: DisplayPrefs = {
      fontScale: isFontScaleStep(record.fontScale) ? record.fontScale : DEFAULT_FONT_SCALE,
      letterSpacing: isLetterSpacing(record.letterSpacing)
        ? record.letterSpacing
        : DEFAULT_LETTER_SPACING,
      lineHeight: isLineHeight(record.lineHeight) ? record.lineHeight : DEFAULT_LINE_HEIGHT,
      fontFamily: isFontFamilyPref(record.fontFamily) ? record.fontFamily : DEFAULT_FONT_FAMILY,
    };
    if (
      prefs.fontScale !== record.fontScale ||
      prefs.letterSpacing !== record.letterSpacing ||
      prefs.lineHeight !== record.lineHeight ||
      prefs.fontFamily !== record.fontFamily ||
      record.direction !== undefined
    ) {
      persistDisplayPrefs(prefs);
    }
    return prefs;
  } catch {
    // Unparseable JSON or storage unavailable — run on defaults. The rewrite
    // is best-effort (persistDisplayPrefs swallows storage failures).
    persistDisplayPrefs(DEFAULT_DISPLAY_PREFS);
    return DEFAULT_DISPLAY_PREFS;
  }
};

/** Root font-size % — everything rem-based scales proportionally. At the
 *  default the property stays UNSET so an untouched root serves v3. */
const applyFontScale = (fontScale: number): void => {
  if (typeof document === 'undefined') return;
  if (fontScale === DEFAULT_FONT_SCALE) {
    document.documentElement.style.removeProperty('font-size');
  } else {
    document.documentElement.style.fontSize = `${fontScale}%`;
  }
};

const applyLetterSpacing = (letterSpacing: LetterSpacing): void => {
  if (typeof document === 'undefined') return;
  if (letterSpacing === DEFAULT_LETTER_SPACING) {
    document.documentElement.style.removeProperty('--ap-letter-spacing');
  } else {
    document.documentElement.style.setProperty(
      '--ap-letter-spacing',
      LETTER_SPACING_EM[letterSpacing]
    );
  }
};

/** Leading accommodation — class on <html> ('normal' = neither class);
 *  tokens.css does the actual var overrides. */
const applyLineHeight = (lineHeight: LineHeight): void => {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  for (const [value, className] of Object.entries(LINE_HEIGHT_CLASS)) {
    el.classList.toggle(className, lineHeight === value);
  }
};

/** Reading-font override — class + var on <html>, exactly like the theme
 *  classes; at 'default' both are removed so the root serves the stock stack
 *  (and the teacher-set dyslexia accommodation, when present, still applies). */
const applyFontFamily = (fontFamily: FontFamilyPref): void => {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  if (fontFamily === DEFAULT_FONT_FAMILY) {
    el.classList.remove(FONT_FAMILY_CLASS);
    el.style.removeProperty(FONT_FAMILY_VAR);
  } else {
    el.style.setProperty(FONT_FAMILY_VAR, FONT_FAMILY_STACKS[fontFamily]);
    el.classList.add(FONT_FAMILY_CLASS);
  }
};

/** Exam/preview-scoped prefs. An absent field falls through to the stored pref. */
export interface DisplayOverride {
  /** Includes 'system': resolved through resolveTheme, so OS changes still track while it is 'system'. */
  theme?: ThemeMode;
  fontScale?: number;
  letterSpacing?: LetterSpacing;
  lineHeight?: LineHeight;
  /** An explicit 'default' is a real choice — it clears `ap-font-pref`. Absent = next tier. */
  fontFamily?: FontFamilyPref;
  /** Experience supplies the font via the dyslexia channel — see applyEffectiveFontFamily. */
  suppressDeviceFontPref?: boolean;
}

interface ThemeState {
  /** User's persisted choice. */
  mode: ThemeMode;
  /** What's applied to <html> after resolving 'system'. */
  resolvedTheme: ResolvedTheme;
  /** Font-size accommodation (%) — always one of FONT_SCALE_STEPS. */
  fontScale: number;
  /** Letter-spacing accommodation. */
  letterSpacing: LetterSpacing;
  /** Line-height accommodation. */
  lineHeight: LineHeight;
  /** Reading-font override — 'default' leaves the root stack untouched. */
  fontFamily: FontFamilyPref;
  /** Active font-scale suspensions (ref count) — see suspendFontScale. */
  fontScaleSuspensions: number;
  displayOverride: DisplayOverride | null;
  /** True once initTheme() has run (DOM applied + system listeners attached). */
  initialized: boolean;
}

interface ThemeActions {
  /** Idempotent. Called once by App — applies theme + accommodations and watches system prefs. */
  initTheme: () => void;
  /** Set + persist + apply. Unknown values degrade to 'light' (the default). */
  setMode: (mode: ThemeMode) => void;
  /** Set + persist + apply. Snaps to the nearest FONT_SCALE_STEPS stop. */
  setFontScale: (fontScale: number) => void;
  /** Set + persist + apply. Unknown values degrade to 'normal'. */
  setLetterSpacing: (letterSpacing: LetterSpacing) => void;
  /** Set + persist + apply. Unknown values degrade to 'normal'. */
  setLineHeight: (lineHeight: LineHeight) => void;
  /** Set + persist + apply. Unknown values degrade to 'default'. */
  setFontFamily: (fontFamily: FontFamilyPref) => void;
  /**
   * STOPGAP for APL-12137 (print's own spacing options will replace this).
   * While a print/export preview is open, the on-screen sheets must match the
   * server-rendered PDF — which never sees the Display font-size %. Suspending
   * pins the root font-size to the default; resuming restores the stored %.
   * Ref-counted so nested surfaces (export dialog + the print page it embeds)
   * each suspend/resume without double-restoring. The stored preference is
   * never changed.
   */
  suspendFontScale: () => void;
  /** Balances one suspendFontScale call; the last resume restores the stored %. */
  resumeFontScale: () => void;
  /** Precedence, highest first: font-scale suspension > override > stored pref. Pass null to drop it. Never persisted. */
  setDisplayOverride: (override: DisplayOverride | null) => void;
}

type ThemeStore = ThemeState & ThemeActions;

const initialMode = readStoredMode();
const initialDisplayPrefs = readStoredDisplayPrefs();

export const useThemeStore = create<ThemeStore>()((set, get) => {
  /** The effective theme CHOICE — an exam override outranks the stored mode. */
  const effectiveMode = (): ThemeMode => get().displayOverride?.theme ?? get().mode;

  /** The only writer of resolvedTheme after init. */
  const applyEffectiveTheme = (): void => {
    const resolved = resolveTheme(effectiveMode());
    if (resolved === get().resolvedTheme) return;
    // applyTheme BEFORE set: subscribers fire on set, and any that read
    // tokens synchronously (blend cache) must see the new theme's DOM state.
    applyTheme(resolved);
    set({ resolvedTheme: resolved });
  };

  /** Gate on the EFFECTIVE choice: an exam override of 'system' must still track the OS. */
  const handleSystemChange = (): void => {
    if (effectiveMode() !== 'system') return;
    applyEffectiveTheme();
  };

  const watchSystemPreferences = (): void => {
    if (!canMatchMedia()) return;
    for (const query of ['(prefers-color-scheme: dark)', '(prefers-contrast: more)']) {
      const mql = window.matchMedia(query);
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handleSystemChange);
      } else if (typeof mql.addListener === 'function') {
        // Safari < 14 fallback
        mql.addListener(handleSystemChange);
      }
    }
  };

  /* The only writers of these DOM properties after init. */
  const applyEffectiveFontScale = (): void => {
    if (get().fontScaleSuspensions > 0) {
      applyFontScale(DEFAULT_FONT_SCALE);
      return;
    }
    applyFontScale(get().displayOverride?.fontScale ?? get().fontScale);
  };
  const applyEffectiveLetterSpacing = (): void =>
    applyLetterSpacing(get().displayOverride?.letterSpacing ?? get().letterSpacing);
  const applyEffectiveLineHeight = (): void =>
    applyLineHeight(get().displayOverride?.lineHeight ?? get().lineHeight);
  /** tokens.css declares `html.ap-font-pref` AFTER `html.dyslexia-font` at equal specificity, so a stale device pref outranks a grant unless suppressed here. */
  const applyEffectiveFontFamily = (): void => {
    const override = get().displayOverride;
    applyFontFamily(
      override?.fontFamily ??
        (override?.suppressDeviceFontPref ? DEFAULT_FONT_FAMILY : get().fontFamily)
    );
  };

  return {
    mode: initialMode,
    resolvedTheme: resolveTheme(initialMode),
    fontScale: initialDisplayPrefs.fontScale,
    letterSpacing: initialDisplayPrefs.letterSpacing,
    lineHeight: initialDisplayPrefs.lineHeight,
    fontFamily: initialDisplayPrefs.fontFamily,
    fontScaleSuspensions: 0,
    displayOverride: null,
    initialized: false,

    initTheme: () => {
      if (get().initialized) return;
      const resolved = resolveTheme(get().mode);
      applyTheme(resolved);
      set({ resolvedTheme: resolved, initialized: true });
      applyEffectiveFontScale();
      applyEffectiveLetterSpacing();
      applyEffectiveLineHeight();
      applyEffectiveFontFamily();
      watchSystemPreferences();
    },

    setMode: (mode) => {
      // Mirror the read path: gate for APPLICATION only — a prod-gated extended
      // mode applies as 'system' but the user's real choice is what persists
      // (never write the downgrade over it; a dev/staging visit keeps it).
      const chosen: ThemeMode = isThemeMode(mode) ? mode : DEFAULT_MODE;
      const safeMode = gateThemeMode(chosen);
      set({ mode: safeMode });
      persistMode(chosen);
      // Through the effective applier, so an active exam override keeps precedence.
      applyEffectiveTheme();
    },

    setFontScale: (fontScale) => {
      const snapped = snapFontScale(fontScale);
      set({ fontScale: snapped });
      persistDisplayPrefs({
        fontScale: snapped,
        letterSpacing: get().letterSpacing,
        lineHeight: get().lineHeight,
        fontFamily: get().fontFamily,
      });
      // During a suspension the new choice is stored but the pinned root
      // font-size stays — resumeFontScale applies it (APL-12137 stopgap).
      applyEffectiveFontScale();
    },

    setLetterSpacing: (letterSpacing) => {
      const safeSpacing = isLetterSpacing(letterSpacing) ? letterSpacing : DEFAULT_LETTER_SPACING;
      set({ letterSpacing: safeSpacing });
      persistDisplayPrefs({
        fontScale: get().fontScale,
        letterSpacing: safeSpacing,
        lineHeight: get().lineHeight,
        fontFamily: get().fontFamily,
      });
      applyEffectiveLetterSpacing();
    },

    setLineHeight: (lineHeight) => {
      const safeLineHeight = isLineHeight(lineHeight) ? lineHeight : DEFAULT_LINE_HEIGHT;
      set({ lineHeight: safeLineHeight });
      persistDisplayPrefs({
        fontScale: get().fontScale,
        letterSpacing: get().letterSpacing,
        lineHeight: safeLineHeight,
        fontFamily: get().fontFamily,
      });
      applyEffectiveLineHeight();
    },

    setFontFamily: (fontFamily) => {
      const safeFamily = isFontFamilyPref(fontFamily) ? fontFamily : DEFAULT_FONT_FAMILY;
      set({ fontFamily: safeFamily });
      persistDisplayPrefs({
        fontScale: get().fontScale,
        letterSpacing: get().letterSpacing,
        lineHeight: get().lineHeight,
        fontFamily: safeFamily,
      });
      applyEffectiveFontFamily();
    },

    suspendFontScale: () => {
      const count = get().fontScaleSuspensions;
      set({ fontScaleSuspensions: count + 1 });
      if (count === 0) applyEffectiveFontScale();
    },

    resumeFontScale: () => {
      const count = get().fontScaleSuspensions;
      if (count === 0) return; // unbalanced resume — nothing to restore
      set({ fontScaleSuspensions: count - 1 });
      if (count === 1) applyEffectiveFontScale();
    },

    setDisplayOverride: (override) => {
      set({ displayOverride: sanitizeDisplayOverride(override) });
      applyEffectiveTheme();
      applyEffectiveFontScale();
      applyEffectiveLetterSpacing();
      applyEffectiveLineHeight();
      applyEffectiveFontFamily();
    },
  };
});

/**
 * Suspend the Display font-size % for this component's lifetime — the
 * effect cleanup guarantees the restore on EVERY exit path (close button,
 * Escape, route change, parent unmount). Stopgap for APL-12137: mount this
 * on any surface that must render at the server PDF's scale.
 */
export const useSuspendFontScaleWhileMounted = (): void => {
  const suspend = useThemeStore(selectSuspendFontScale);
  const resume = useThemeStore(selectResumeFontScale);
  useEffect(() => {
    suspend();
    return resume;
  }, [suspend, resume]);
};

// Field-level selectors (.neo/rules/frontend.md § Zustand — narrow selectors)
export const selectThemeMode = (state: ThemeStore): ThemeMode => state.mode;
export const selectResolvedTheme = (state: ThemeStore): ResolvedTheme => state.resolvedTheme;
export const selectInitTheme = (state: ThemeStore): (() => void) => state.initTheme;
export const selectSetThemeMode = (state: ThemeStore): ((mode: ThemeMode) => void) => state.setMode;
export const selectFontScale = (state: ThemeStore): number => state.fontScale;
export const selectLetterSpacing = (state: ThemeStore): LetterSpacing => state.letterSpacing;
export const selectSetFontScale = (state: ThemeStore): ((fontScale: number) => void) =>
  state.setFontScale;
export const selectSetLetterSpacing = (
  state: ThemeStore
): ((letterSpacing: LetterSpacing) => void) => state.setLetterSpacing;
export const selectLineHeight = (state: ThemeStore): LineHeight => state.lineHeight;
export const selectSetLineHeight = (state: ThemeStore): ((lineHeight: LineHeight) => void) =>
  state.setLineHeight;
export const selectFontFamily = (state: ThemeStore): FontFamilyPref => state.fontFamily;
export const selectSetFontFamily = (state: ThemeStore): ((fontFamily: FontFamilyPref) => void) =>
  state.setFontFamily;
export const selectSuspendFontScale = (state: ThemeStore): (() => void) => state.suspendFontScale;
export const selectResumeFontScale = (state: ThemeStore): (() => void) => state.resumeFontScale;
export const selectSetDisplayOverride = (
  state: ThemeStore
): ((override: DisplayOverride | null) => void) => state.setDisplayOverride;
export const selectDisplayOverride = (state: ThemeStore): DisplayOverride | null =>
  state.displayOverride;
